
import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Quiz } from "@/components/quiz/types";
import { useSession } from "@/hooks/useSession";
import { useTrainingProgress } from "@/hooks/useTrainingProgress";

export function useQuizData(resourceId?: string) {
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [isLoadingQuiz, setIsLoadingQuiz] = useState(true);
  const [hasAttemptedQuizFetch, setHasAttemptedQuizFetch] = useState(false);
  const [quizAttempt, setQuizAttempt] = useState<any>(null);
  const [quizFileId, setQuizFileId] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const { session } = useSession();
  
  const { 
    isCompleted: quizCompleted, 
    markAsCompleted: markQuizCompleted 
  } = useTrainingProgress(resourceId, quizFileId);

  const fetchQuiz = useCallback(async () => {
    if (!resourceId) {
      console.log('No resourceId provided to fetchQuiz');
      setIsLoadingQuiz(false);
      setError("No resource ID provided");
      return;
    }
    
    try {
      setIsLoadingQuiz(true);
      setError(null);
      console.log('Fetching quiz for resource:', resourceId);
      
      // First get the quiz file for this resource
      const { data: quizFiles, error: quizFilesError } = await supabase
        .from("resource_files")
        .select(`
          id,
          title,
          description,
          quiz_description,
          passing_score,
          quiz_id,
          resource_id,
          created_at
        `)
        .eq("resource_id", resourceId)
        .eq("file_type", "quiz")
        .order("order_index")
        .maybeSingle();

      if (quizFilesError) {
        console.error("Supabase error fetching quiz file:", quizFilesError);
        setError(`Error fetching quiz file: ${quizFilesError.message}`);
        throw quizFilesError;
      }

      console.log('Quiz file data received:', quizFiles);

      if (!quizFiles || !quizFiles.quiz_id) {
        console.warn('No quiz file or quiz_id found for resource:', resourceId);
        setError("No quiz found for this resource");
        setIsLoadingQuiz(false);
        return;
      }

      setQuizFileId(quizFiles.id);
      
      // Now get the quiz and its questions in a single query
      // Fix: Modify the order query to use a simpler approach
      const { data: quizData, error: quizError } = await supabase
        .from("quizzes")
        .select(`
          id,
          title,
          description,
          passing_score,
          resource_id,
          created_at,
          quiz_questions (
            id,
            quiz_id,
            question,
            type,
            correct_answer,
            options,
            order_index,
            created_at
          )
        `)
        .eq("id", quizFiles.quiz_id)
        .single();

      if (quizError) {
        console.error("Supabase error fetching quiz:", quizError);
        setError(`Error fetching quiz: ${quizError.message}`);
        throw quizError;
      }

      console.log('Quiz data received:', quizData);
      
      if (!quizData) {
        console.warn('No quiz data found for quiz ID:', quizFiles.quiz_id);
        setError("Quiz data not found");
        setIsLoadingQuiz(false);
        return;
      }
      
      // Sort questions by order_index manually instead of in the query
      if (quizData.quiz_questions) {
        quizData.quiz_questions.sort((a, b) => a.order_index - b.order_index);
      }
      
      // Format the quiz data
      const formattedQuiz: Quiz = {
        id: quizData.id,
        title: quizFiles.title || quizData.title,
        description: quizFiles.quiz_description || quizFiles.description || quizData.description,
        passing_score: quizFiles.passing_score || quizData.passing_score || 70,
        resource_id: resourceId,
        created_at: quizData.created_at,
        quiz_questions: quizData.quiz_questions || []
      };
      
      console.log('Formatted quiz data:', formattedQuiz);
      setQuiz(formattedQuiz);
      
      // Check for existing quiz attempts
      if (session?.user?.id) {
        const { data: attemptData, error: attemptError } = await supabase
          .from('quiz_attempts')
          .select('*')
          .eq('trainee_id', session.user.id)
          .eq('quiz_id', quizFiles.quiz_id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
          
        if (attemptError && attemptError.code !== 'PGRST116') {
          console.error("Error fetching quiz attempt:", attemptError);
        } else if (attemptData) {
          console.log('Quiz attempt data:', attemptData);
          setQuizAttempt(attemptData);
        }
      }
    } catch (error: any) {
      console.error("Error fetching quiz:", error);
      setError(error?.message || "Failed to load quiz details");
      toast({
        title: "Error",
        description: "Failed to load quiz details",
        variant: "destructive",
      });
    } finally {
      setIsLoadingQuiz(false);
      setHasAttemptedQuizFetch(true);
    }
  }, [resourceId, toast, session?.user?.id]);
  
  const updateQuizCompletion = async (quizId: string, attemptData: any) => {
    if (!session?.user?.id || !attemptData || !attemptData.passed || !quizFileId || !resourceId) {
      console.log('Missing required data for updateQuizCompletion:', {
        userId: session?.user?.id ? 'present' : 'missing',
        attemptData: attemptData ? 'present' : 'missing',
        passed: attemptData?.passed ? 'yes' : 'no',
        quizFileId: quizFileId ? 'present' : 'missing',
        resourceId: resourceId ? 'present' : 'missing'
      });
      return;
    }
    
    console.log('Updating quiz completion with attempt:', attemptData);
    
    try {
      // Ensure quizId is a string and not an array or other type
      if (typeof quizId !== 'string') {
        console.error('Invalid quiz ID type:', typeof quizId);
        throw new Error('Quiz ID must be a string');
      }
      
      console.log('Using quiz ID for insert:', quizId);
      
      const { error: attemptError } = await supabase
        .from('quiz_attempts')
        .insert({
          quiz_id: quizId,
          trainee_id: session.user.id,
          started_at: new Date().toISOString(),
          completed_at: new Date().toISOString(),
          score: attemptData.score,
          passed: attemptData.passed,
          responses: attemptData.responses
        });

      if (attemptError) {
        console.error('Error inserting quiz attempt:', attemptError);
        throw attemptError;
      }
      
      setQuizAttempt(attemptData);
      await markQuizCompleted();
      
      await supabase
        .from('resource_shares')
        .update({ quiz_completed: true })
        .eq('resource_id', resourceId)
        .eq('trainee_id', session.user.id);
        
      console.log('Resource shares updated successfully');
    } catch (error: any) {
      console.error('Error updating quiz completion:', error);
      setError(`Failed to update quiz completion: ${error.message}`);
    }
  };
  
  useEffect(() => {
    if (resourceId && session?.user?.id && !hasAttemptedQuizFetch) {
      fetchQuiz();
    }
  }, [fetchQuiz, resourceId, session?.user?.id, hasAttemptedQuizFetch]);

  return {
    quiz,
    isLoadingQuiz,
    hasAttemptedQuizFetch,
    setHasAttemptedQuizFetch,
    fetchQuiz,
    quizCompleted,
    quizAttempt,
    updateQuizCompletion,
    error
  };
}
